import { cssRaw, style } from 'typestyle';
import { type CSSProperties, type NestedCSSProperties } from 'typestyle/lib/types';
import DashboardTheme from '../statistics/dashboard/dashboard-theme';
import StyleConstants from '../../core/styles.constants';
import { tint } from '../../core/admin-surface.styles';

// Access tab chrome, following the approved access mockup. The card shell, the search field and
// the buttons/inputs come from `core/admin-surface.styles`, which every mockup-styled admin
// surface shares; only the pieces this mockup adds on top live here.

// The table sits flush inside the card, so it carries no border or radius of its own — unlike the
// standalone table wrapper in `office-table.styles`.
export const tableWrap = style({
  overflowX: 'auto',
  minWidth: 0,
});

cssRaw(
  `
  .${tableWrap} tbody tr {
    transition: background-color .12s ease;
  }
  .${tableWrap} tbody tr:hover {
    background-color: ${DashboardTheme.gray50};
  }
  `,
);

// Revealed by the card head button: the add-address field never takes up room until it is needed.
export const addRow = (isMobile: boolean): CSSProperties => ({
  display: 'flex',
  flexWrap: 'wrap',
  flexDirection: isMobile ? 'column' : 'row',
  gap: 8,
  padding: '14px 20px',
  background: tint(StyleConstants.DARK_BLUE_COLOR, 0.04),
  borderBottom: `1px solid ${DashboardTheme.gray200}`,
});

// The add row holds three fields now, and `textInput` is `flex: 1` with `minWidth: 0`, so without
// these they would divide the row into three equal columns and squeeze the address - the one field
// that is always filled in and the longest - into a third of the space. A basis plus a floor keeps
// the address roomier than the names and makes the row wrap onto a second line rather than
// shrinking past the point of being usable.
export const addRowEmailField: CSSProperties = {
  flexGrow: 2,
  flexBasis: 240,
  minWidth: 200,
};

export const addRowNameField: CSSProperties = {
  flexGrow: 1,
  flexBasis: 130,
  minWidth: 120,
};

// Sits on its own line under the fields, like the inline error, but reads as information rather
// than as a problem: the administrator has done nothing wrong, the name is simply already known.
export const addRowHint: CSSProperties = {
  flexBasis: '100%',
  fontFamily: DashboardTheme.bodyFontFamily,
  fontSize: 12.5,
  color: DashboardTheme.gray700,
};

// The inline error has to sit on its own line under the field, not next to the buttons.
export const addRowError: CSSProperties = {
  flexBasis: '100%',
};

// A scheduled departure is a state of the person, not a value in a cell, so it is drawn as a badge
// rather than left as a bare date. Reading a date in a column means noticing that the column is not
// empty; a badge is visible at a glance across a page of thirty rows, which is what an
// administrator scanning for "who is leaving" is actually doing.
export const accessEndBadge: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  borderRadius: 999,
  padding: '3px 10px',
  border: `1px solid ${tint(StyleConstants.ORANGE_COLOR, 0.35)}`,
  background: tint(StyleConstants.ORANGE_COLOR, 0.12),
  color: DashboardTheme.gray700,
  fontFamily: DashboardTheme.bodyFontFamily,
  fontSize: 12.5,
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

// Amber rather than red on purpose. Nothing has gone wrong and nothing is cancelled yet — the
// person is still working here — so the row should draw the eye without reading as an error.
export const scheduledRow: CSSProperties = {
  background: tint(StyleConstants.ORANGE_COLOR, 0.05),
};

// Sits next to the search field and carries its own count, so "three people are leaving" is legible
// without turning the filter on.
export const scheduledFilterButton = (active: boolean): NestedCSSProperties => ({
  border: `1px solid ${active ? StyleConstants.ORANGE_COLOR : DashboardTheme.gray300}`,
  background: active ? tint(StyleConstants.ORANGE_COLOR, 0.12) : StyleConstants.WHITE_COLOR,
  color: DashboardTheme.gray700,
  borderRadius: 10,
  padding: '9px 14px',
  fontFamily: DashboardTheme.bodyFontFamily,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  $nest: {
    '&:hover': {
      background: tint(StyleConstants.ORANGE_COLOR, 0.18),
    },
  },
});

export const nameWithBadge: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
};

// Medical bag in the row, so first aiders stand out without a column of their own.
export const medicBadge: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  color: StyleConstants.PINK_COLOR,
};

// The listing's medic toggle, lit when the person already is one.
export const medicActionActive: NestedCSSProperties = {
  color: StyleConstants.PINK_COLOR,
  borderColor: tint(StyleConstants.PINK_COLOR, 0.45),
  background: tint(StyleConstants.PINK_COLOR, 0.08),
};

export const filterButtonContent: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
};

export const foot: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 16,
  flexWrap: 'wrap',
  padding: '12px 20px',
  borderTop: `1px solid ${DashboardTheme.gray200}`,
  fontFamily: DashboardTheme.bodyFontFamily,
  fontSize: 12.5,
  color: DashboardTheme.gray700,
};

export const footGroup: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

// Administrators list.
export const adminAddRow = (isMobile: boolean): CSSProperties => ({
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  gap: 8,
  padding: '20px 20px 8px',
});

export const adminAddError: CSSProperties = {
  padding: '0 20px 4px',
};

export const adminList: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: '0 20px 8px',
};

export const adminRow: NestedCSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 0',
  borderBottom: `1px solid ${DashboardTheme.gray200}`,
  $nest: {
    '&:last-child': {
      borderBottom: 0,
    },
  },
};

export const adminWho: CSSProperties = {
  flex: 1,
  minWidth: 0,
};

export const adminEmail: CSSProperties = {
  display: 'block',
  fontSize: 13.5,
  fontWeight: 600,
  color: StyleConstants.DARK_BLUE_COLOR,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export const ownerChip: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  marginTop: 4,
  height: 20,
  padding: '0 8px',
  borderRadius: 10,
  background: StyleConstants.DARK_BLUE_COLOR,
  color: StyleConstants.WHITE_COLOR,
  fontSize: 11,
  fontWeight: 700,
  whiteSpace: 'nowrap',
};

// Two actions per row now — hand over ownership, and remove administrator rights — so they need a
// container of their own rather than sitting as the row's last child.
export const adminActions: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  marginLeft: 'auto',
};

export const cardPadded: CSSProperties = {
  padding: '0 20px 20px',
};

export const loaderPad: CSSProperties = {
  padding: '24px 0',
};

// A stacked form field inside a modal.
//
// `admin-surface.textInput` is shaped for the Access tab's add-row, which is a `Row`: it carries
// `flex: 1` so the email and name fields share the width between them. Reused unchanged inside a
// `Column`, that same `flex: 1` applies to the *vertical* axis instead and each input stretches to
// fill the dialog, which is what made the first and last name fields grow into one another. So the
// modal variant cancels the flex and takes the full width outright.
export const modalTextInput: NestedCSSProperties = {
  flex: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

// Label above its input, left-aligned. `modal.styles.bodyText` centres its text, which is right for
// a sentence addressed to the reader and wrong for a field label sitting over a left-aligned box.
export const modalFieldLabel: CSSProperties = {
  display: 'block',
  marginBottom: 4,
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 600,
  fontFamily: DashboardTheme.bodyFontFamily,
  color: DashboardTheme.gray500,
};

// What the switch in the edit dialog turns on, said beside it in the body's own voice.
export const modalSwitchText: CSSProperties = {
  textAlign: 'left',
  fontSize: 14,
  fontFamily: DashboardTheme.bodyFontFamily,
  color: DashboardTheme.gray700,
};

// The label-plus-input pair. The gap belongs on the group rather than between every child of the
// dialog, so the prose above and the buttons below keep their own rhythm.
// Separates the per-office fields from the global name above them, and says which is which.
export const modalSectionNote: CSSProperties = {
  display: 'block',
  margin: '4px 0 12px',
  textAlign: 'left',
  fontSize: 12.5,
  fontFamily: DashboardTheme.bodyFontFamily,
  color: DashboardTheme.gray500,
};

export const modalField: CSSProperties = {
  display: 'block',
  width: '100%',
  marginBottom: 12,
};

// Holds the fields apart from the prose above them without inheriting the dialog's centring.
export const modalForm: CSSProperties = {
  width: '100%',
  marginTop: 8,
  textAlign: 'left',
};
